import React from "react";
import classnames from "classnames";
import {
  formatSessionDate,
  formatAttendancePercentage,
} from "../../../utils/ZoomAttendance.utils";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../../constants/ZoomAttendance.constants";
import { AttendanceRingSVG } from "./AttendanceRingSVG";

export const useAttendanceRingColor = (percentage) => {
  const pct = Math.round(parseFloat(percentage || 0));

  if (pct >= ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_THRESHOLDS.HIGH) {
    return {
      strokeColorClass: "tw-text-emerald-500",
      textColorClass: "tw-text-emerald-700",
    };
  }

  if (pct > ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_THRESHOLDS.ZERO) {
    return {
      strokeColorClass: "tw-text-amber-500",
      textColorClass: "tw-text-amber-700",
    };
  }

  return {
    strokeColorClass: "tw-text-rose-500",
    textColorClass: "tw-text-rose-700",
  };
};

export const SessionColumn = ({ record }) => (
  <div className="tw-flex tw-flex-col">
    <span className="tw-font-bold tw-text-gray-900 tw-text-sm">
      {`${ZOOM_ATTENDANCE_CONSTANTS.LABELS.SESSION_PREFIX} ${record.session_number}`}
    </span>
    <span className="tw-text-xs tw-text-gray-500 tw-mt-1">
      {formatSessionDate(record.start_time, record.end_time)}
    </span>
  </div>
);

export const StatusColumn = ({ record }) => {
  const isCompleted =
    record.status === ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_STATUS.COMPLETED;

  const statusClassNames = classnames(
    "tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-capitalize",
    {
      "tw-bg-emerald-100 tw-text-emerald-800": isCompleted,
      "tw-bg-amber-100 tw-text-amber-800": !isCompleted,
    }
  );

  return <span className={statusClassNames}>{record.status}</span>;
};

export const TotalAttendanceRing = ({ record }) => {
  const { strokeColorClass, textColorClass } = useAttendanceRingColor(
    record?.attendance_percentage
  );

  if (
    record?.status !== ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_STATUS.COMPLETED
  ) {
    return (
      <span className="tw-text-sm tw-font-medium tw-text-gray-500">
        {ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA}
      </span>
    );
  }

  const pct = Math.round(parseFloat(record.attendance_percentage || 0));
  const pctFormatted = formatAttendancePercentage(record.attendance_percentage);
  const totalAttendedCount = record.total_attended ?? 0;

  return (
    <div
      className="tw-group tw-relative tw-inline-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-cursor-pointer"
      title={pctFormatted}
    >
      <AttendanceRingSVG pct={pct} strokeColorClass={strokeColorClass} />

      {/* Default: show attendance count inside circle */}
      <span
        className={classnames(
          "tw-absolute tw-text-[11px] tw-font-bold group-hover:tw-hidden",
          textColorClass
        )}
      >
        {totalAttendedCount}
      </span>

      {/* Hover: show percentage inside circle */}
      <span
        className={classnames(
          "tw-absolute tw-text-[10px] tw-font-bold tw-hidden group-hover:tw-block",
          textColorClass
        )}
      >
        {pctFormatted}
      </span>
    </div>
  );
};

export const AttendanceTypeCount = ({ record, field, textClass }) => {
  if (record.status !== ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_STATUS.COMPLETED) {
    return ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;
  }
  return (
    <span className={classnames("tw-text-sm tw-font-bold", textClass)}>
      {record[field]}
    </span>
  );
};

export const ViewAttendeesAction = ({ record, onViewAttendees }) => {
  if (record.status !== ZOOM_ATTENDANCE_CONSTANTS.ATTENDANCE_STATUS.COMPLETED) {
    return ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_DATA;
  }
  return (
    <button
      type="button"
      onClick={() => onViewAttendees(record)}
      className="tw-text-sm tw-font-semibold tw-text-indigo-700 hover:tw-text-indigo-900 tw-transition-colors tw-border-none tw-bg-transparent focus:tw-outline-none"
    >
      {ZOOM_ATTENDANCE_CONSTANTS.LABELS.VIEW_ATTENDEES}
    </button>
  );
};
